import { assignCommonProperties } from '../util/data/assign-common-properties.js'

import type { UserIdType } from '../types.js'
import { UserIdCtor } from '../user-id.js'
import { JsonMappable } from '../data/json-mappable.js'
import {
	jsonClass,
	jsonProperty,
	jsonArrayProperty,
} from '@sumbricht/json-class-serializer'
import { UserIdpRole } from './user-role.js'
import { UserRole } from './user-role.js'
import { UserPassword } from './user-password.js'
import type { SystemRoleName } from './system-roles.js'
import { UserPermission } from './user-permission.js'
import { SystemPermissionName } from './system-permissions.js'
import { UserRecord, UserRecordOptions } from './user-record.js'
import { UserConfiguration } from './user-configuration.js'
import { UserRecordUpdate } from './user-record-update-schema.js'

export const UserStatuses = [
	'active',
	'passwordChangeNeeded',
	'blocked',
	'inactive',
	'deleted',
] as const
export type UserStatus = (typeof UserStatuses)[number]

@jsonClass('User')
export class User implements JsonMappable, Required<UserRecordUpdate> {
	@jsonProperty(String)
	get __type(): string {
		return 'User'
	}
	set __type(_) {
		/*ignore*/
	}

	@jsonProperty(() => UserIdCtor)
	id: UserIdType

	@jsonProperty(String)
	userName: string = ''

	@jsonProperty(String)
	firstName: string = ''

	@jsonProperty(String)
	lastName: string = ''

	@jsonProperty(String)
	email: string = ''

	@jsonProperty(String)
	mobilePhone: string = ''

	@jsonProperty(Date)
	creationTimestamp: Date = new Date()

	@jsonProperty(Date)
	updateTimestamp: Date = new Date()

	@jsonProperty(Date)
	lastLogin?: Date

	// @jsonProperty(() => UserPassword)
	password?: UserPassword

	@jsonArrayProperty(() => UserRole)
	roles?: UserRole[]
	@jsonArrayProperty(() => UserIdpRole)
	idpRoles?: UserIdpRole[]

	@jsonArrayProperty(() => UserPermission)
	permissions?: UserPermission[]

	@jsonArrayProperty(() => UserConfiguration)
	configurations?: UserConfiguration[]

	@jsonArrayProperty(String)
	groups?: string[]

	@jsonProperty(String)
	status: UserStatus = 'active'

	@jsonProperty(Number)
	failedLoginAttempts: number = 0

	@jsonArrayProperty(() => UserRole)
	effectiveRoles?: UserRole[]
	@jsonArrayProperty(() => UserPermission)
	effectivePermissions?: UserPermission[]
	identityProvider: never
	@jsonProperty(String)
	identityProviderId?: string
	@jsonProperty(String)
	identityProviderUserId?: string
	@jsonProperty(String)
	managerUserId?: string

	get systemRoles() {
		return (
			(this.effectiveRoles ?? [...this.roles!, ...this.idpRoles!])?.filter(
				(r) => r.roleModuleId == 'System',
			) ?? []
		)
	}

	get systemPermissions() {
		return (
			(this.effectivePermissions ?? this.permissions)?.filter(
				(p) => p.permissionModuleId == 'System',
			) ?? []
		)
	}

	constructor(init?: Partial<User>) {
		assignCommonProperties(this, init)
	}

	init() {
		if (!this.roles) this.roles = []
		if (!this.permissions) this.permissions = []
	}

	deepClone(): User {
		return new User({
			...this,
			roles: this.roles?.map((role) => new UserRole(role)) ?? [],
			idpRoles: this.idpRoles?.map((role) => new UserIdpRole(role)) ?? [],
			password: new UserPassword(this.password),
		})
	}

	getName() {
		return `${this.firstName} ${this.lastName}`
	}

	getRoleNames(filterForTenantId?: string) {
		let roles = this.effectiveRoles ?? [...this.roles!, ...this.idpRoles!]
		if (filterForTenantId)
			roles = roles.filter((r) => r.tenantIds.includes(filterForTenantId))
		const roleNames = roles.map((role) => role.getQualifiedName()) ?? []
		return [...new Set(roleNames)]
	}

	private hasRoleInternal(role: UserRole | string, roleList: UserRole[]) {
		if (typeof role == 'string') role = UserRole.fromQualifiedName(role)
		return roleList?.some((r) => r.matches(role))
	}

	hasEffectiveRole(role: UserRole | string) {
		return this.hasRoleInternal(role, this.effectiveRoles ?? this.roles ?? [])
	}

	hasDirectRole(role: UserRole | string) {
		return this.hasRoleInternal(role, this.roles ?? [])
	}

	hasIdpRole(role: UserIdpRole | string) {
		return this.hasRoleInternal(role, this.idpRoles ?? [])
	}

	hasSystemRole(roleName: SystemRoleName) {
		const roles = this.effectiveRoles ?? [...this.roles!, ...this.idpRoles!]
		return !!roles.find(
			(role) => role.roleModuleId == 'System' && role.roleBoId == roleName,
		)
	}

	getRoleInheritanceSources(role: UserRole) {
		if (this.hasDirectRole(role) || !this.hasEffectiveRole(role)) return []

		const sources: string[] = []
		if (this.hasIdpRole(role)) sources.push('From identity provider')
		if (!sources.length) sources.push('From other roles')
		return sources
	}

	getPermissionNames(filterForTenantId?: string) {
		let permissions = this.effectivePermissions ?? this.permissions ?? []
		if (filterForTenantId)
			permissions = permissions.filter((r) =>
				r.tenantIds.includes(filterForTenantId),
			)
		const permissionNames =
			permissions.map((permission) => permission.getQualifiedName()) ?? []
		return [...new Set(permissionNames)]
	}

	hasDirectPermission(permission: UserPermission | string) {
		if (typeof permission == 'string')
			permission = UserPermission.fromQualifiedName(permission)
		const permissions = this.permissions ?? []
		return !!permissions.some((p) => p.matches(permission))
	}

	hasEffectivePermission(permission: UserPermission | string) {
		if (typeof permission == 'string')
			permission = UserPermission.fromQualifiedName(permission)
		const permissions = this.effectivePermissions ?? this.permissions ?? []
		return !!permissions.some((p) => p.matches(permission))
	}
	hasPermission(permission: UserPermission | string) {
		return this.hasEffectivePermission(permission)
	}

	hasSystemPermission(permissionName: SystemPermissionName) {
		const permissions = this.effectivePermissions ?? this.permissions ?? []
		return !!permissions.find(
			(permission) =>
				permission.permissionModuleId == 'System' &&
				permission.permissionBoId == permissionName,
		)
	}

	isActive() {
		return this.status == 'active'
	}

	canLogin() {
		return this.status == 'active' || this.status == 'passwordChangeNeeded'
	}

	canResetPassword() {
		return this.canLogin() || this.status == 'blocked'
	}

	isInGroup(groupName: string) {
		return this.groups?.includes(groupName) ?? false
	}

	mapToUserRecord(
		options?: UserRecordOptions,
		getManager?: (subordinate: User) => User | undefined,
	) {
		if (options == '*') {
			options = {
				includeEmail: true,
				includeGroups: true,
				includePermissions: true,
				includePhone: true,
				includeRoles: true,
				includeManager: true,
			}
		}

		let manager: UserRecord | undefined
		if (options?.includeManager && this.managerUserId && getManager) {
			let managerOptions: UserRecordOptions
			if (options.includeManager === true) {
				managerOptions = {
					includeEmail: options.includeEmail,
					includePhone: options.includePhone,
				}
			} else {
				managerOptions = options.includeManager
			}
			const managerUser = getManager(this)
			manager = managerUser?.mapToUserRecord(managerOptions, getManager)
		}

		return new UserRecord({
			...this,
			email: options?.includeEmail ? this.email : undefined,
			mobilePhone: options?.includePhone ? this.mobilePhone : undefined,
			roleNames: options?.includeRoles ? this.getRoleNames() : undefined,
			permissionNames: options?.includePermissions
				? this.getPermissionNames()
				: undefined,
			groupNames: options?.includeGroups ? (this.groups ?? []) : undefined,
			manager,
		})
	}

	cloneWithoutPassword() {
		return new User({
			...this,
			password: undefined,
		})
	}

	toUserIdAndName() {
		const data = {
			userId: this.id,
			name: this.getName(),
		}

		const { UserIdAndName } = (globalThis as any).System ?? {}
		if (UserIdAndName) {
			return new UserIdAndName(data)
		}
		return data
	}
}
